// Praca bez internetu w aplikacji ze sklepu (docs/pwa/NATYWNA.md 4 „Offline”, zakres - właściciel 29.10): odpowiedzi
// ekranów przepisów i planów, instrukcji, alarmów, historii procesów (i tego, bez czego APP nie wystartuje: konto,
// pulpity) oraz migawka stanu urządzeń z bramy (brama/polaczenie.ts) zapisane w telefonie i podane, gdy zaplecze nie
// odpowiada. Zapas pobiera z wyprzedzeniem api/zapas.ts (także nieotwierane ekrany).
// Zamiast SQLCipher z planu (bez nowej wtyczki natywnej - dochodzi paczką APP): każdy wpis szyfrowany AES-GCM w
// IndexedDB aplikacji, klucz w Keychain / Keystore (SecureStorage, jak żeton) - plików nie da się otworzyć poza APP.
// Wpis ważny 30 dni; kasowany przy wylogowaniu (sesja.tsx), przy odmowie dostępu do tej ścieżki (brak_dostepu /
// nie_ma) i po terminie. Przepisy biblioteki dodatkowo pilnuje koniec subskrypcji (BibliotekaOffline) - po nim bez
// sieci też nie są wydawane i giną. Przeglądarka - nic (offline tylko w aplikacji).
import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import { natywna } from "../uklad/instalacja";

const WAZNOSC_MS = 30 * 24 * 3600_000;
const KLUCZ_SZYFRU = "offline_klucz";
const BAZA = "twf-offline";
const ZBIOR = "wpisy";

/** Ścieżki zapisywane do pracy bez internetu (zapytanie GET i plik). */
const ZAKRES = [/^\/api\/konto$/, /^\/api\/pulpity/, /^\/api\/przepisy/, /^\/api\/plany/, /^\/api\/instrukcje/,
  /^\/api\/alarmy/, /^\/brama\/urzadzenia\/[^/]+\/(przebiegi|probki)/];

export const wZakresie = (sciezka: string) => natywna() && ZAKRES.some((r) => r.test(sciezka));

interface Wpis { klucz: string; czas: number; iv: Uint8Array; dane: ArrayBuffer }

let szyfr: Promise<CryptoKey> | null = null;

function kluczSzyfru(): Promise<CryptoKey> {
  szyfr ??= (async () => {
    let surowy = await SecureStorage.getItem(KLUCZ_SZYFRU).catch(() => null);
    if (!surowy) {
      surowy = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
      await SecureStorage.setItem(KLUCZ_SZYFRU, surowy);
    }
    return crypto.subtle.importKey("raw", Uint8Array.from(atob(surowy), (c) => c.charCodeAt(0)), "AES-GCM", false, ["encrypt", "decrypt"]);
  })();
  szyfr.catch(() => { szyfr = null; });
  return szyfr;
}

function baza(): Promise<IDBDatabase> {
  return new Promise((ok, nie) => {
    const r = indexedDB.open(BAZA, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(ZBIOR, { keyPath: "klucz" });
    r.onsuccess = () => ok(r.result);
    r.onerror = () => nie(r.error);
  });
}

async function zbior<T>(tryb: IDBTransactionMode, f: (z: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await baza();
  return new Promise((ok, nie) => {
    const r = f(db.transaction(ZBIOR, tryb).objectStore(ZBIOR));
    r.onsuccess = () => ok(r.result);
    r.onerror = () => nie(r.error);
  }).finally(() => db.close()) as Promise<T>;
}

/** Zapis odpowiedzi (bajty) pod kluczem - błąd zapisu nie psuje ekranu. */
export async function zapamietaj(klucz: string, dane: ArrayBuffer): Promise<void> {
  try {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const szyfrogram = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, await kluczSzyfru(), dane);
    await zbior("readwrite", (z) => z.put({ klucz, czas: Date.now(), iv, dane: szyfrogram } satisfies Wpis));
    await bezStarszychProbek(klucz);
  } catch { /* bez miejsca / bez Keystore - po prostu bez offline */ }
}

// Historia próbek (`/brama/urzadzenia/{sn}/probki?od&do`): zakres wykresu liczony od „teraz” (24 h, 7 / 30 dni, rok),
// więc każde otwarcie to inny adres - bez sieci podawany jest zapis tej sztuki najlepiej pokrywający zakres.
const PROBKI = /^(\/brama\/urzadzenia\/[^/]+\/probki)\?od=(\d+)&do=(\d+)$/;

interface ZakresProbek { klucz: string; od: number; do: number }

async function zakresyProbek(baza: string): Promise<ZakresProbek[]> {
  const klucze = await zbior<IDBValidKey[]>("readonly", (z) => z.getAllKeys(IDBKeyRange.bound(baza + "?", baza + "?￿"))).catch(() => []);
  return klucze.flatMap((k) => {
    const m = PROBKI.exec(String(k));
    return m ? [{ klucz: String(k), od: Number(m[2]), do: Number(m[3]) }] : [];
  });
}

/** Nowy zapis próbek zastępuje starsze zapisy tego samego okna (ponowne otwarcie „24 h” itp.) - zawarte w nowym
 *  zakresie i podobnej długości (±10%); drobniejsze okna (wykres przebiegu) zostają. */
async function bezStarszychProbek(klucz: string): Promise<void> {
  const m = PROBKI.exec(klucz);
  if (!m?.[1]) return;
  const od = Number(m[2]), doS = Number(m[3]), dlugosc = doS - od;
  for (const z of await zakresyProbek(m[1])) {
    if (z.klucz !== klucz && z.od >= od - dlugosc * 0.1 && z.do <= doS && z.do - z.od >= dlugosc * 0.9) await zapomnij(z.klucz);
  }
}

/** Próbki zakresu bez własnego zapisu: zapis tej sztuki z największą częścią wspólną (przy remisie - krótszy, czyli
 *  drobniejszy krok), przycięty do zakresu. */
async function probkiZakresu(klucz: string): Promise<{ dane: ArrayBuffer; czas: number } | null> {
  const m = PROBKI.exec(klucz);
  if (!m?.[1]) return null;
  const od = Number(m[2]), doS = Number(m[3]);
  const wspolne = (z: ZakresProbek) => Math.min(z.do, doS) - Math.max(z.od, od);
  const wybor = (await zakresyProbek(m[1])).filter((z) => wspolne(z) > 0)
    .sort((a, b) => wspolne(b) - wspolne(a) || (a.do - a.od) - (b.do - b.od))[0];
  const w = wybor && await odczytaj(wybor.klucz);
  if (!w) return null;
  const d = JSON.parse(zTekstu.decode(w.dane)) as { t: number[]; v: (number | null)[][]; znaczniki: { t: number }[] };
  const ind = d.t.flatMap((t, i) => (t >= od && t <= doS ? [i] : []));
  const zakres = { ...d, t: ind.map((i) => d.t[i]), v: d.v.map((k) => ind.map((i) => k[i] ?? null)),
    znaczniki: d.znaczniki.filter((z) => z.t >= od && z.t <= doS) };
  return { dane: tekst.encode(JSON.stringify(zakres)).buffer as ArrayBuffer, czas: w.czas };
}

/** Przepisy biblioteki w telefonie per dziedzina (zapisuje api/zapas.ts): koniec subskrypcji (unix s) i numery
 *  przepisów - po końcu ich pliki bez sieci nie są wydawane, a listy pokazują bibliotekę jak bez subskrypcji. */
export type BibliotekaOffline = Partial<Record<string, { do: number; ids: number[] }>>;
export const KLUCZ_BIBLIOTEKI = "przepisy/biblioteka";

const PLIK_PRZEPISU = /^\/api\/przepisy\/(\d+)\//;

const parametr = (klucz: string, nazwa: string) => new URLSearchParams(klucz.split("?")[1] ?? "").get(nazwa);

/** Adres przepisu biblioteki (plik) albo listy przepisów dziedziny, której subskrypcja już się skończyła. */
async function poSubskrypcji(klucz: string): Promise<boolean> {
  if (!klucz.startsWith("/api/przepisy")) return false;
  const b = (await odczytajJson<BibliotekaOffline>(KLUCZ_BIBLIOTEKI))?.dane;
  const teraz = Date.now() / 1000;
  const id = Number(PLIK_PRZEPISU.exec(klucz)?.[1] ?? 0);
  return Object.entries(b ?? {}).some(([dziedzina, d]) => !!d && d.do <= teraz
    && (id ? d.ids.includes(id) : parametr(klucz, "dziedzina") === dziedzina));
}

/** Zapisana odpowiedź i jej czas (ms); null - brak albo starsza niż 30 dni (wtedy kasowana). */
export async function odczytaj(klucz: string): Promise<{ dane: ArrayBuffer; czas: number } | null> {
  try {
    const w = await zbior<Wpis | undefined>("readonly", (z) => z.get(klucz));
    if (!w) return null;
    if (Date.now() - w.czas > WAZNOSC_MS || (PLIK_PRZEPISU.test(klucz) && await poSubskrypcji(klucz))) {
      await zapomnij(klucz);
      return null;
    }
    return { dane: await crypto.subtle.decrypt({ name: "AES-GCM", iv: w.iv as BufferSource }, await kluczSzyfru(), w.dane), czas: w.czas };
  } catch {
    return null;
  }
}

/** Czas zapisu wpisu (ms) bez odszyfrowania; 0 - brak. Zapas nie pobiera drugi raz świeżych plików. */
export async function czasWpisu(klucz: string): Promise<number> {
  try {
    return (await zbior<Wpis | undefined>("readonly", (z) => z.get(klucz)))?.czas ?? 0;
  } catch {
    return 0;
  }
}

export async function zapomnij(klucz: string): Promise<void> {
  await zbior("readwrite", (z) => z.delete(klucz)).catch(() => undefined);
}

/** Wszystkie wpisy, których adres zaczyna się od `prefiks` (pliki przepisu po końcu subskrypcji). */
export async function zapomnijPrefiks(prefiks: string): Promise<void> {
  await zbior("readwrite", (z) => z.delete(IDBKeyRange.bound(prefiks, prefiks + "￿"))).catch(() => undefined);
}

/** Wylogowanie: wszystko precz (dane i klucz - następne konto zaczyna od zera). */
export async function wyczyscOffline(): Promise<void> {
  if (!natywna()) return;
  szyfr = null;
  await SecureStorage.removeItem(KLUCZ_SZYFRU).catch(() => undefined);
  await new Promise<void>((ok) => {
    const r = indexedDB.deleteDatabase(BAZA);
    r.onsuccess = r.onerror = r.onblocked = () => ok();
  });
}

const tekst = new TextEncoder();
const zTekstu = new TextDecoder();

export const zapamietajJson = (klucz: string, dane: unknown) => zapamietaj(klucz, tekst.encode(JSON.stringify(dane)).buffer as ArrayBuffer);

const PRZEBIEGI = /^(\/brama\/urzadzenia\/[^/]+\/przebiegi)\?(.*)$/;

/** Lista przebiegów z zakresem dat (podsumowanie okresu w Statystykach - granice liczone od „teraz”, więc adres za
 *  każdym razem inny): zapis tej samej strony bez dat, zawężony w telefonie do przebiegów z zakresu. */
async function przebiegiZakresu(klucz: string): Promise<{ dane: ArrayBuffer; czas: number } | null> {
  const m = PRZEBIEGI.exec(klucz);
  if (!m?.[1]) return null;
  const q = new URLSearchParams(m[2]);
  if (!q.has("od") && !q.has("do")) return null;
  const od = Number(q.get("od") ?? -Infinity), doS = Number(q.get("do") ?? Infinity);
  q.delete("od");
  q.delete("do");
  const w = await odczytaj(`${m[1]}?${q}`);
  if (!w) return null;
  const d = JSON.parse(zTekstu.decode(w.dane)) as { przebiegi: { start: number }[] };
  const zakres = { ...d, przebiegi: d.przebiegi.filter((p) => p.start >= od && p.start < doS), wiecej: false };
  return { dane: tekst.encode(JSON.stringify(zakres)).buffer as ArrayBuffer, czas: w.czas };
}

/** Lista przepisów po końcu subskrypcji: biblioteka pusta, dostęp nieaktywny (jak odpowiedź serwera bez subskrypcji). */
function bezBiblioteki<T>(klucz: string, d: T): T {
  const l = d as { dostep?: object; sekcje?: object; sekcja?: object };
  const pusta = { przepisy: [], wszystkie: 0 };
  if (l.dostep) l.dostep = { ...l.dostep, aktywny: false, wkrotce: false, dni: 0 };
  if (l.sekcje) l.sekcje = { ...l.sekcje, library: pusta };
  if (l.sekcja && parametr(klucz, "rodzaj") === "library") l.sekcja = pusta;
  return d;
}

export async function odczytajJson<T>(klucz: string): Promise<{ dane: T; czas: number } | null> {
  const w = (await odczytaj(klucz)) ?? (await przebiegiZakresu(klucz)) ?? (await probkiZakresu(klucz));
  if (!w) return null;
  const dane = JSON.parse(zTekstu.decode(w.dane)) as T;
  return { dane: await poSubskrypcji(klucz) ? bezBiblioteki(klucz, dane) : dane, czas: w.czas };
}
